import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { chatService, parseServerTimestamp, MY_TELEGRAM_ID } from './api/chats';
import { chatsCacheService } from './api/chatsCache';
import { authService } from './api/auth';
import { socketService } from './api/socket';
import Login from './components/Login';
import ChatList from './components/ChatList';
import ChatWindow from './components/ChatWindow';
import { Chat, Message, MessageReaction } from './types';
import './App.css';

// Define ProtectedRoute outside to prevent remounts
const ProtectedRoute: React.FC<{ children: React.ReactNode; isAuthenticated: boolean }> = ({ children, isAuthenticated }) => {
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

// Define LoginRoute to redirect authenticated users away from login page
const LoginRoute: React.FC<{ children: React.ReactNode; isAuthenticated: boolean }> = ({ children, isAuthenticated }) => {
  return isAuthenticated ? <Navigate to="/" /> : <>{children}</>;
};

const normalizeBotSenderName = (value?: string) => {
  if (!value) return value;
  const normalized = value.replace(/^@/, '').trim().toLowerCase();
  if (normalized === 'innoprog_tuto_bot' || normalized === 'innoprog_tutor_bot') return '@Куратор';
  return value;
};

const isSelfSender = (senderId?: number, senderName?: string) => {
  if (senderId === MY_TELEGRAM_ID) return true;
  if (!senderName) return false;
  const normalized = senderName.replace(/^@/, '').trim().toLowerCase();
  return normalized === 'bot';
};

const shouldDeferPreviewText = (text: string) => {
  if (!text) return false;
  if (text.includes('{{')) return false;
  return /(^|\s)@[a-z0-9_]{3,}/i.test(text);
};

const DEFAULT_ALLOWED_REACTIONS = ['👍', '❤️', '🔥', '🤝','🎉', '👏', '😁', '🤔', '😢', '🙏', '😎'];

const normalizeReactionsPayload = (raw: any): MessageReaction[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((reaction) => {
      const emoji = reaction?.emoji ?? reaction?.reaction ?? reaction?.code ?? reaction?.symbol;
      if (!emoji) return null;
      let count: number | undefined = reaction.count ?? reaction.total ?? reaction.amount;
      if (typeof count !== 'number') {
        const users = reaction.user_ids ?? reaction.users;
        if (Array.isArray(users)) count = users.length;
      }
      const reactedByMe = reaction.is_mine ?? reaction.reacted_by_me ?? reaction.is_my ?? reaction.me;
      return {
        emoji: String(emoji),
        count: typeof count === 'number' && !Number.isNaN(count) ? count : 1,
        reactedByMe: typeof reactedByMe === 'boolean' ? reactedByMe : undefined
      };
    }).filter(Boolean) as MessageReaction[];
  }
  if (typeof raw === 'object') {
    return Object.entries(raw).map(([emoji, count]) => ({
      emoji: String(emoji),
      count: typeof count === 'number' ? count : Number(count),
      reactedByMe: false
    })).filter((item) => !Number.isNaN(item.count) && item.count > 0);
  }
  return [];
};

const extractBackendError = (error: any): string => {
  const data = error?.response?.data;
  if (!data) return '';
  if (typeof data === 'string') return data;
  if (typeof data?.detail === 'string') return data.detail;
  if (Array.isArray(data?.detail)) return data.detail.join('\n');
  if (typeof data?.message === 'string') return data.message;
  if (typeof data?.error === 'string') return data.error;
  return '';
};

type ChatPageProps = {
  chats: Chat[];
  selectedChatId: string | null;
  setSelectedChatId: (chatId: string | null) => void;
  messages: Record<string, Message[]>;
  draftsByChat: Record<string, { text: string; html: string }>;
  onDraftChange: (chatId: string, draft: { text: string; html: string } | null) => void;
  onSendMessage: (text: string, attachments?: File[], replyToMessageId?: number) => void;
  onEditMessage: (chatId: string, messageId: number, text: string) => Promise<void>;
  onDeleteMessage: (chatId: string, messageId: number) => Promise<void>;
  onToggleReaction: (chatId: string, message: Message, emoji: string) => void;
  allowedReactions: string[];
  onUpdateUnreadCount: (chatId: string, unreadCount: number) => void;
  onLoadOlderMessages: (chatId: string, offset: number, limit: number) => Promise<number>;
  onLogout: () => void;
  isChatsLoading: boolean;
  hasLoadedChats: boolean;
};

const ChatPage: React.FC<ChatPageProps> = ({
  chats,
  selectedChatId,
  setSelectedChatId,
  messages,
  draftsByChat,
  onDraftChange,
  onSendMessage,
  onEditMessage,
  onDeleteMessage,
  onToggleReaction,
  allowedReactions,
  onUpdateUnreadCount,
  onLoadOlderMessages,
  onLogout,
  isChatsLoading,
  hasLoadedChats
}) => {
  const navigate = useNavigate();
  const { chatId } = useParams();
  const [noAccessChatId, setNoAccessChatId] = useState<string | null>(null);

  useEffect(() => {
    if (!chatId) {
      setSelectedChatId(null);
      setNoAccessChatId(null);
      return;
    }

    if ((isChatsLoading && chats.length === 0) || (!hasLoadedChats && chats.length === 0)) return;

    const chatExists = chats.some(chat => chat.id === chatId);
    if (chatExists) {
      setSelectedChatId(chatId);
      setNoAccessChatId(null);
    } else {
      setSelectedChatId(null);
      setNoAccessChatId(chatId);
    }
  }, [chatId, chats, isChatsLoading, setSelectedChatId]);

  const handleSelectChat = (chatId: string) => {
    navigate(`/chat/${chatId}`);
  };

  const handleBackToChatList = () => {
    navigate('/');
  };

  const handleCloseNoAccess = () => {
    setNoAccessChatId(null);
    navigate('/');
  };

  useEffect(() => {
    if (noAccessChatId) {
      document.body.classList.add('mini-app-open');
    } else {
      document.body.classList.remove('mini-app-open');
    }
  }, [noAccessChatId]);

  const [sidebarWidth, setSidebarWidth] = useState(500);
  const [isResizing, setIsResizing] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const resizerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const startResizing = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const stopResizing = React.useCallback(() => {
    setIsResizing(false);
  }, []);

  const resize = React.useCallback((e: MouseEvent) => {
    if (isResizing) {
      const newWidth = e.clientX;
      if (newWidth > 300 && newWidth < 800) {
        setSidebarWidth(newWidth);
      }
    }
  }, [isResizing]);

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', resize);
      window.addEventListener('mouseup', stopResizing);
    } else {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    }
    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [isResizing, resize, stopResizing]);

  const selectedChat = chats.find(chat => chat.id === selectedChatId) || null;
  const currentMessages = selectedChatId ? messages[selectedChatId] || [] : [];
  const showChatWindow = Boolean(selectedChatId);
  const selectedDraft = selectedChatId ? draftsByChat[selectedChatId] ?? null : null;

  return (
    <div className={`chat-app ${showChatWindow ? 'show-chat-window' : ''}`}>
      <div
        className="chat-list-container"
        style={{ width: !isMobile ? `${sidebarWidth}px` : undefined }}
      >
        <ChatList
          chats={chats}
          messages={messages}
          draftsByChat={draftsByChat}
          selectedChatId={selectedChatId}
          onSelectChat={handleSelectChat}
          onLogout={onLogout}
          isLoading={isChatsLoading}
        />
      </div>
      <div
        className={`sidebar-resizer ${isResizing ? 'is-resizing' : ''}`}
        onMouseDown={startResizing}
      />
      <ChatWindow
        chat={selectedChat}
        messages={currentMessages}
        onSendMessage={onSendMessage}
        onEditMessage={onEditMessage}
        onDeleteMessage={onDeleteMessage}
        onToggleReaction={onToggleReaction}
        allowedReactions={allowedReactions}
        onDraftChange={onDraftChange}
        draft={selectedDraft}
        onUpdateUnreadCount={onUpdateUnreadCount}
        onLoadOlderMessages={onLoadOlderMessages}
        onBack={handleBackToChatList}
      />
      {noAccessChatId && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal">
            <h3>Нет доступа к чату</h3>
            <p>У вас нет доступа к этому чату.</p>
            <button className="modal-button" onClick={handleCloseNoAccess}>Ок</button>
          </div>
        </div>
      )}
    </div>
  );
};

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('isAuthenticated') === 'true';
  });
  const [chats, setChats] = useState<Chat[]>([]);
  const [isChatsLoading, setIsChatsLoading] = useState(false);
  const [hasLoadedChats, setHasLoadedChats] = useState(false);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Record<string, Message[]>>({});
  const [draftsByChat, setDraftsByChat] = useState<Record<string, { text: string; html: string }>>(() => {
    try {
      const raw = localStorage.getItem('chatDrafts');
      if (!raw) return {};
      const parsed = JSON.parse(raw) as Record<string, { text: string; html: string }>;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      console.warn('Failed to load drafts from storage', error);
      return {};
    }
  });
  const [blockedReactions, setBlockedReactions] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem('blockedReactions');
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('Failed to load blocked reactions', error);
      return [];
    }
  });
  const [myReactionsByMessage, setMyReactionsByMessage] = useState<Record<string, string[]>>(() => {
    try {
      const raw = localStorage.getItem('myReactionsByMessage');
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      console.warn('Failed to load my reactions', error);
      return {};
    }
  });
  const isRefreshingStatusesRef = useRef(false);
  const lastMessageFetchRef = useRef(new Map<string, Promise<void>>());
  const lastMessagePendingRef = useRef(new Map<string, boolean>());

  // Ref to access current chats in socket listener without triggering reconnects
  const chatsRef = React.useRef(chats);
  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);
  const selectedChatIdRef = React.useRef<string | null>(selectedChatId);
  useEffect(() => {
    selectedChatIdRef.current = selectedChatId;
  }, [selectedChatId]);

  // Загружает чаты из кэша или с сервера (только при первой загрузке)
  const loadChats = React.useCallback(async (forceRefresh: boolean = false) => {
    try {
      setIsChatsLoading(true);
      const fetchedChats = await chatsCacheService.getChats(forceRefresh);
      setChats(fetchedChats);
    } catch (error) {
      console.error('Failed to load chats', error);
    } finally {
      setIsChatsLoading(false);
      setHasLoadedChats(true);
    }
  }, []);

  const refreshChatLastMessage = React.useCallback(async (chatId: string) => {
    if (!chatId) return;
    const inFlight = lastMessageFetchRef.current.get(chatId);
    if (inFlight) {
      lastMessagePendingRef.current.set(chatId, true);
      return inFlight;
    }

    const task = (async () => {
      try {
        const latestMessages = await chatService.getMessagesPage(chatId, 1, 0);
        if (!latestMessages || latestMessages.length === 0) return;
        const latestMessage = latestMessages[latestMessages.length - 1];

        setChats(prevChats => {
          const updatedChats = prevChats.map(chat => {
            if (chat.id !== chatId) return chat;
            const current = chat.lastMessage;
            if (current?.telegramMessageId && latestMessage.telegramMessageId) {
              if (current.telegramMessageId > latestMessage.telegramMessageId) {
                return chat;
              }
            } else if (current?.timestamp && latestMessage.timestamp) {
              if (current.timestamp.getTime() > latestMessage.timestamp.getTime()) {
                return chat;
              }
            }
            return {
              ...chat,
              lastMessage: latestMessage
            };
          });
          chatsCacheService.updateChat(chatId, (chat) => ({
            ...chat,
            lastMessage: latestMessage
          }));
          return updatedChats;
        });
      } catch (error) {
        console.error('Failed to refresh last message', error);
      } finally {
        lastMessageFetchRef.current.delete(chatId);
        const hasPending = lastMessagePendingRef.current.get(chatId);
        if (hasPending) {
          lastMessagePendingRef.current.delete(chatId);
          void refreshChatLastMessage(chatId);
        }
      }
    })();

    lastMessageFetchRef.current.set(chatId, task);
    return task;
  }, []);

  // Локально обновляет чат при новом сообщении (без перезагрузки всех чатов)
  const updateChatLocally = React.useCallback((
    telegramChatId: number,
    lastMessageText: string,
    senderName: string,
    timestamp: Date,
    senderId?: number,
    senderRawName?: string
  ) => {
    const isMe = isSelfSender(senderId, senderName);
    const previewPending = shouldDeferPreviewText(lastMessageText);
    const previewText = previewPending ? '' : lastMessageText;
    const displaySender = isMe ? 'me' : (normalizeBotSenderName(senderName) || senderName);
    const displayRaw = isMe ? senderRawName : (normalizeBotSenderName(senderRawName) || senderRawName);
    setChats(prevChats => {
      const updatedChats = prevChats.map(chat => {
        if (chat.telegramChatId === telegramChatId) {
          return {
            ...chat,
            lastMessage: {
              id: `temp-${Date.now()}`,
              text: previewText,
              sender: displaySender,
              senderId,
              senderRawName: displayRaw,
              timestamp,
              isRead: false,
              isPreviewPending: previewPending
            },
            unreadCount: (chat.unreadCount || 0) + 1
          };
        }
        return chat;
      });
      // Обновляем кэш
      chatsCacheService.updateChatByTelegramId(telegramChatId, (chat) => {
        const updatedChat = updatedChats.find(c => c.telegramChatId === telegramChatId);
        return updatedChat || chat;
      });
      return updatedChats;
    });
  }, []);

  const matchesMessageIdentifiers = React.useCallback((
    message: Message,
    identifiers: { id?: string | number; telegramMessageId?: number }
  ) => {
    if (identifiers.telegramMessageId !== undefined && message.telegramMessageId === identifiers.telegramMessageId) {
      return true;
    }
    if (identifiers.id !== undefined && String(message.id) === String(identifiers.id)) {
      return true;
    }
    return false;
  }, []);

  const getReactionMessageKey = (chatId: string, identifiers: { id?: string | number; telegramMessageId?: number }) => {
    if (identifiers.telegramMessageId !== undefined) return `${chatId}:tg:${identifiers.telegramMessageId}`;
    if (identifiers.id !== undefined) return `${chatId}:id:${identifiers.id}`;
    return `${chatId}:unknown`;
  };

  const syncMyReactionsForMessage = React.useCallback((
    chatId: string,
    identifiers: { id?: string | number; telegramMessageId?: number },
    reactions: MessageReaction[]
  ) => {
    const key = getReactionMessageKey(chatId, identifiers);
    const allowed = new Set(reactions.map((reaction) => reaction.emoji));
    setMyReactionsByMessage((prev) => {
      const current = prev[key] ?? [];
      const filtered = current.filter((emoji) => allowed.has(emoji));
      if (filtered.length === current.length) return prev;
      const next = { ...prev };
      if (filtered.length === 0) {
        delete next[key];
      } else {
        next[key] = filtered;
      }
      return next;
    });
  }, []);

  const applyMyReactions = React.useCallback((
    chatId: string,
    identifiers: { id?: string | number; telegramMessageId?: number },
    reactions: MessageReaction[]
  ) => {
    const key = getReactionMessageKey(chatId, identifiers);
    const mySet = new Set(myReactionsByMessage[key] ?? []);
    if (mySet.size === 0) return reactions;
    return reactions.map((reaction) =>
      mySet.has(reaction.emoji) ? { ...reaction, reactedByMe: true } : reaction
    );
  }, [myReactionsByMessage]);

  useEffect(() => {
    try {
      if (Object.keys(myReactionsByMessage).length === 0) {
        localStorage.removeItem('myReactionsByMessage');
      } else {
        localStorage.setItem('myReactionsByMessage', JSON.stringify(myReactionsByMessage));
      }
    } catch (error) {
      console.warn('Failed to persist my reactions', error);
    }
  }, [myReactionsByMessage]);

  // Memoize fetchMessages
  const fetchMessages = React.useCallback(async (chatId: string): Promise<Message[] | null> => {
    try {
      const fetchedMessages = await chatService.getMessages(chatId);
      setMessages(prev => ({
        ...prev,
        [chatId]: fetchedMessages
      }));
      fetchedMessages.forEach((message) => {
        if (!message.reactions || message.reactions.length === 0) return;
        syncMyReactionsForMessage(chatId, { id: message.id, telegramMessageId: message.telegramMessageId }, message.reactions);
      });
      return fetchedMessages;
    } catch (error) {
      console.error("Failed to fetch messages", error);
      return null;
    }
  }, [syncMyReactionsForMessage]);

  const loadOlderMessages = React.useCallback(async (chatId: string, offset: number, limit: number) => {
    try {
      const fetchedMessages = await chatService.getMessagesPage(chatId, limit, offset);
      if (fetchedMessages.length === 0) return 0;
      let addedCount = 0;
      setMessages(prev => {
        const existing = prev[chatId] || [];
        const existingIds = new Set(existing.map(msg => msg.id));
        const toAdd = fetchedMessages.filter(msg => !existingIds.has(msg.id));
        addedCount = toAdd.length;
        if (toAdd.length === 0) return prev;
        return {
          ...prev,
          [chatId]: [...toAdd, ...existing]
        };
      });
      fetchedMessages.forEach((message) => {
        if (!message.reactions || message.reactions.length === 0) return;
        syncMyReactionsForMessage(chatId, { id: message.id, telegramMessageId: message.telegramMessageId }, message.reactions);
      });
      return addedCount;
    } catch (error) {
      console.error("Failed to load older messages", error);
      return 0;
    }
  }, [syncMyReactionsForMessage]);

  const updateMessageTextLocally = React.useCallback((
    chatId: string,
    identifiers: { id?: string | number; telegramMessageId?: number },
    text: string,
    editedAt?: Date
  ) => {
    setMessages(prev => {
      const current = prev[chatId];
      if (!current) return prev;
      const updatedMessages = current.map(message =>
        matchesMessageIdentifiers(message, identifiers)
          ? { ...message, text, editedAt: editedAt ?? message.editedAt }
          : message
      );
      return {
        ...prev,
        [chatId]: updatedMessages
      };
    });

    setChats(prevChats => {
      let didUpdate = false;
      const updatedChats = prevChats.map(chat => {
        if (chat.id !== chatId || !chat.lastMessage) return chat;
        if (!matchesMessageIdentifiers(chat.lastMessage, identifiers)) return chat;
        didUpdate = true;
        return {
          ...chat,
          lastMessage: {
            ...chat.lastMessage,
            text,
            editedAt: editedAt ?? chat.lastMessage.editedAt
          }
        };
      });

      if (didUpdate) {
        chatsCacheService.updateChat(chatId, (chat) => {
          if (!chat.lastMessage || !matchesMessageIdentifiers(chat.lastMessage, identifiers)) return chat;
          return {
            ...chat,
            lastMessage: {
              ...chat.lastMessage,
              text,
              editedAt: editedAt ?? chat.lastMessage.editedAt
            }
          };
        });
      }

      return updatedChats;
    });
  }, [matchesMessageIdentifiers]);

  const updateMessageReactionsLocally = React.useCallback((
    chatId: string,
    identifiers: { id?: string | number; telegramMessageId?: number },
    reactions: MessageReaction[]
  ) => {
    const merged = applyMyReactions(chatId, identifiers, reactions);
    setMessages(prev => {
      const current = prev[chatId];
      if (!current) return prev;
      let didUpdate = false;
      const updatedMessages = current.map(message => {
        if (!matchesMessageIdentifiers(message, identifiers)) return message;
        didUpdate = true;
        return { ...message, reactions: merged };
      });
      if (!didUpdate) return prev;
      return {
        ...prev,
        [chatId]: updatedMessages
      };
    });

    setChats(prevChats => {
      let didUpdate = false;
      const updatedChats = prevChats.map(chat => {
        if (chat.id !== chatId || !chat.lastMessage) return chat;
        if (!matchesMessageIdentifiers(chat.lastMessage, identifiers)) return chat;
        didUpdate = true;
        return {
          ...chat,
          lastMessage: {
            ...chat.lastMessage,
            reactions: merged
          }
        };
      });

      if (didUpdate) {
        chatsCacheService.updateChat(chatId, (chat) => {
          if (!chat.lastMessage || !matchesMessageIdentifiers(chat.lastMessage, identifiers)) return chat;
          return {
            ...chat,
            lastMessage: {
              ...chat.lastMessage,
              reactions: merged
            }
          };
        });
      }

      return updatedChats;
    });
  }, [applyMyReactions, matchesMessageIdentifiers]);

  const removeMessageLocally = React.useCallback((
    chatId: string,
    identifiers: { id?: string | number; telegramMessageId?: number }
  ) => {
    let shouldRefreshLastMessage = false;
    setMessages(prev => {
      const current = prev[chatId];
      const hadTarget = current ? current.some(message => matchesMessageIdentifiers(message, identifiers)) : false;
      const filtered = current ? current.filter(message => !matchesMessageIdentifiers(message, identifiers)) : null;
      const nextLastMessage = filtered && filtered.length > 0 ? filtered[filtered.length - 1] : undefined;
      const removedWasLastInCurrent = Boolean(current && current.length > 0 && matchesMessageIdentifiers(current[current.length - 1], identifiers));

      setChats(prevChats => {
        let didUpdate = false;
        const updatedChats = prevChats.map(chat => {
          if (chat.id !== chatId || !chat.lastMessage) return chat;
          if (!matchesMessageIdentifiers(chat.lastMessage, identifiers)) return chat;
          didUpdate = true;
          return {
            ...chat,
            lastMessage: nextLastMessage
          };
        });

        if (didUpdate) {
          chatsCacheService.updateChat(chatId, (chat) => {
            if (!chat.lastMessage || !matchesMessageIdentifiers(chat.lastMessage, identifiers)) return chat;
            return { ...chat, lastMessage: nextLastMessage };
          });
        }

        if (didUpdate && (!nextLastMessage || !hadTarget)) {
          shouldRefreshLastMessage = true;
        }

        return updatedChats;
      });

      if (!shouldRefreshLastMessage && removedWasLastInCurrent) {
        setChats(prevChats => {
          let didUpdate = false;
          const updatedChats = prevChats.map(chat => {
            if (chat.id !== chatId) return chat;
            didUpdate = true;
            return { ...chat, lastMessage: nextLastMessage };
          });
          if (didUpdate) {
            chatsCacheService.updateChat(chatId, (chat) => ({ ...chat, lastMessage: nextLastMessage }));
          }
          return updatedChats;
        });
        if (!nextLastMessage) {
          shouldRefreshLastMessage = true;
        }
      }

      if (!current || !filtered) return prev;
      return {
        ...prev,
        [chatId]: filtered
      };
    });
    if (shouldRefreshLastMessage) {
      void refreshChatLastMessage(chatId);
    }
  }, [matchesMessageIdentifiers, refreshChatLastMessage]);

  // Обновляет статус конкретного чата при заходе в него
  const refreshChatStatus = React.useCallback(async (chatId: string) => {
    // Use chatsRef to avoid dependency on chats array, preventing infinite loops
    const chat = chatsRef.current.find(c => c.id === chatId);
    if (!chat) return;

    try {
      const { statusColor, lessonsTotal, lessonsScheduled } = await chatService.refreshChatStatus(chat);
      setChats(prevChats => {
        const updatedChats = prevChats.map(c => {
          if (c.id === chatId) {
            return { ...c, statusColor, lessonsTotal, lessonsScheduled };
          }
          return c;
        });
        // Обновляем кэш
        chatsCacheService.updateChat(chatId, (c) => ({ ...c, statusColor, lessonsTotal, lessonsScheduled }));
        return updatedChats;
      });
    } catch (error) {
      console.error('Failed to refresh chat status', error);
    }
  }, []);

  const refreshAllChatStatuses = React.useCallback(async () => {
    if (isRefreshingStatusesRef.current) return;
    const chatsSnapshot = chatsRef.current;
    if (chatsSnapshot.length === 0) return;

    isRefreshingStatusesRef.current = true;
    try {
      let updates = new Map<string, { statusColor: Chat['statusColor']; lessonsTotal?: number; lessonsScheduled?: number }>();
      try {
        updates = await chatService.refreshChatStatusesBatch(chatsSnapshot);
      } catch (error) {
        console.error('Failed to refresh chat statuses', error);
      }

      if (updates.size > 0) {
        setChats(prevChats => prevChats.map(chat => {
          const update = updates.get(chat.id);
          return update ? { ...chat, ...update } : chat;
        }));

        updates.forEach((update, chatId) => {
          chatsCacheService.updateChat(chatId, (chat) => ({ ...chat, ...update }));
        });
      }
    } finally {
      isRefreshingStatusesRef.current = false;
    }
  }, []);

  const updateUnreadCount = React.useCallback((chatId: string, unreadCount: number) => {
    setChats(prevChats => {
      const updatedChats = prevChats.map(chat =>
        chat.id === chatId ? { ...chat, unreadCount } : chat
      );
      chatsCacheService.updateChat(chatId, (chat) => ({ ...chat, unreadCount }));
      return updatedChats;
    });
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          // Verify token is valid
          await authService.getMe();
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Auth check failed', error);
          localStorage.removeItem('token');
          localStorage.removeItem('isAuthenticated');
          setIsAuthenticated(false);
        }
      }
    };
    checkAuth();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    const cachedChats = chatsCacheService.getCachedChats();
    if (cachedChats.length > 0) {
      setChats(cachedChats);
      setHasLoadedChats(true);
    }
    loadChats(true);
  }, [isAuthenticated, loadChats]);

  useEffect(() => {
    if (!isAuthenticated) return;

    void refreshAllChatStatuses();
    const intervalId = window.setInterval(() => {
      void refreshAllChatStatuses();
    }, 300000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isAuthenticated, refreshAllChatStatuses]);

  useEffect(() => {
    try {
      if (Object.keys(draftsByChat).length === 0) {
        localStorage.removeItem('chatDrafts');
      } else {
        localStorage.setItem('chatDrafts', JSON.stringify(draftsByChat));
      }
    } catch (error) {
      console.warn('Failed to persist drafts', error);
    }
  }, [draftsByChat]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const token = localStorage.getItem('token'); // Ensure token is available here
    if (token) {
      socketService.connect(token);

      const removeListener = socketService.onMessage((payload: any) => {
        // Payload structure: { "event": "new_message", "data": { ...message } }
        if (payload?.event === 'new_message' && payload.data) {
          const messageData = payload.data;
          if (messageData.edited_at) {
            const targetChat = chatsRef.current.find(chat => chat.telegramChatId === messageData.telegram_chat_id);
            if (targetChat) {
              updateMessageTextLocally(
                targetChat.id,
                { id: messageData.id, telegramMessageId: messageData.telegram_message_id },
                messageData.text || '',
                messageData.edited_at ? parseServerTimestamp(messageData.edited_at) : undefined
              );
            }
            return;
          }

          // Локально обновляем чат (без перезагрузки всех чатов)
          updateChatLocally(
            messageData.telegram_chat_id,
            messageData.text || (messageData.message_type === 'photo' ? '📷 Фото' : '📎 Файл'),
            messageData.sender_name || 'Unknown',
            parseServerTimestamp(messageData.created_at),
            messageData.sender_id,
            messageData.sender_name
          );

          const targetChat = chatsRef.current.find(c => c.telegramChatId === messageData.telegram_chat_id);
          if (targetChat) {
            void refreshChatLastMessage(targetChat.id);
          }

          // Check if the message belongs to the currently selected chat
          if (selectedChatIdRef.current) {
            // Find the active chat using our ref to avoid stale state
            const activeChat = chatsRef.current.find(c => c.id === selectedChatIdRef.current);

            // If the active chat corresponds to the incoming message's telegram_chat_id, refresh
            if (activeChat && activeChat.telegramChatId === messageData.telegram_chat_id) {
              fetchMessages(activeChat.id);
            }
          }
        } else if (payload?.event === 'new_message') {
          // Fallback: если данные в другом формате, обновляем сообщения выбранного чата
          if (selectedChatIdRef.current && payload.chat_id === selectedChatIdRef.current) {
            fetchMessages(selectedChatIdRef.current);
          }
        } else if (payload?.event === 'message_edited' && payload.data) {
          const messageData = payload.data;
          const targetChat = chatsRef.current.find(chat => chat.telegramChatId === messageData.telegram_chat_id);
          if (!targetChat) return;
          updateMessageTextLocally(
            targetChat.id,
            { id: messageData.id, telegramMessageId: messageData.telegram_message_id },
            messageData.text || '',
            messageData.edited_at ? parseServerTimestamp(messageData.edited_at) : undefined
          );
        } else if (payload?.event === 'message_deleted' && payload.data) {
          const messageData = payload.data;
          const targetChat = chatsRef.current.find(chat => chat.telegramChatId === messageData.telegram_chat_id);
          if (!targetChat) return;
          removeMessageLocally(targetChat.id, { telegramMessageId: messageData.telegram_message_id });
        } else if (payload?.event === 'reactions_updated' && payload.data) {
          const reactionData = payload.data;
          const targetChat = chatsRef.current.find(chat =>
            (reactionData.chat_id && chat.id === reactionData.chat_id) ||
            (reactionData.telegram_chat_id && chat.telegramChatId === reactionData.telegram_chat_id)
          );
          if (!targetChat) return;
          const reactions = normalizeReactionsPayload(reactionData.reactions ?? reactionData);
          syncMyReactionsForMessage(
            targetChat.id,
            {
              id: reactionData.message_id ?? reactionData.id,
              telegramMessageId: reactionData.telegram_message_id ?? reactionData.telegramMessageId
            },
            reactions
          );
          updateMessageReactionsLocally(
            targetChat.id,
            {
              id: reactionData.message_id ?? reactionData.id,
              telegramMessageId: reactionData.telegram_message_id ?? reactionData.telegramMessageId
            },
            reactions
          );
        }
      });

      return () => {
        removeListener();
        socketService.disconnect();
      };
    }
  }, [isAuthenticated, fetchMessages, updateChatLocally, updateMessageTextLocally, updateMessageReactionsLocally, syncMyReactionsForMessage, removeMessageLocally, refreshChatLastMessage]);

  // При выборе чата загружаем сообщения и обновляем статус
  useEffect(() => {
    if (selectedChatId) {
      fetchMessages(selectedChatId);
      refreshChatStatus(selectedChatId);
    }
    // refreshChatStatus is stable (no dependencies), so not needed in deps array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChatId, fetchMessages]);

  const handleEditMessage = React.useCallback(async (chatId: string, messageId: number, text: string) => {
    const updatedMessage = await chatService.updateMessage(chatId, messageId, text);
    updateMessageTextLocally(
      chatId,
      { id: updatedMessage.id, telegramMessageId: updatedMessage.telegramMessageId },
      updatedMessage.text,
      updatedMessage.editedAt
    );
  }, [updateMessageTextLocally]);

  const handleDeleteMessage = React.useCallback(async (chatId: string, messageId: number) => {
    await chatService.deleteMessage(chatId, messageId);
    removeMessageLocally(chatId, { telegramMessageId: messageId });
  }, [removeMessageLocally]);

  const getReactionMessageId = (message: Message): number | null => {
    if (typeof message.telegramMessageId === 'number') return message.telegramMessageId;
    const numericId = Number(message.id);
    return Number.isNaN(numericId) ? null : numericId;
  };

  const applyReactionToggle = (reactions: MessageReaction[], emoji: string) => {
    const index = reactions.findIndex((reaction) => reaction.emoji === emoji);
    if (index === -1) {
      return { next: [...reactions, { emoji, count: 1, reactedByMe: true }], removed: false };
    }
    const current = reactions[index];
    const reactedByMe = Boolean(current.reactedByMe);
    if (reactedByMe) {
      const nextCount = Math.max(0, current.count - 1);
      if (nextCount === 0) {
        return { next: reactions.filter((_, idx) => idx !== index), removed: true };
      }
      const next = [...reactions];
      next[index] = { ...current, count: nextCount, reactedByMe: false };
      return { next, removed: true };
    }
    const next = [...reactions];
    next[index] = { ...current, count: current.count + 1, reactedByMe: true };
    return { next, removed: false };
  };

  const handleToggleReaction = React.useCallback(async (chatId: string, message: Message, emoji: string) => {
    const messageId = getReactionMessageId(message);
    if (messageId === null) return;

    const previous = message.reactions ?? [];
    const currentMyReaction = previous.find((reaction) => reaction.reactedByMe);
    const isSwitching = currentMyReaction && currentMyReaction.emoji !== emoji;
    const { next, removed } = isSwitching
      ? (() => {
          const updated = previous.map((reaction) => {
            if (reaction.emoji === currentMyReaction.emoji) {
              const nextCount = Math.max(0, reaction.count - 1);
              return { ...reaction, count: nextCount, reactedByMe: false };
            }
            if (reaction.emoji === emoji) {
              return { ...reaction, count: reaction.count + 1, reactedByMe: true };
            }
            return reaction;
          }).filter((reaction) => reaction.count > 0);
          if (!updated.some((reaction) => reaction.emoji === emoji)) {
            updated.push({ emoji, count: 1, reactedByMe: true });
          }
          return { next: updated, removed: true };
        })()
      : applyReactionToggle(previous, emoji);
    const key = getReactionMessageKey(chatId, { id: message.id, telegramMessageId: message.telegramMessageId });
    setMyReactionsByMessage((prev) => {
      const current = prev[key] ?? [];
      const set = new Set(current);
      if (isSwitching && currentMyReaction) {
        set.delete(currentMyReaction.emoji);
        set.add(emoji);
      } else if (removed) {
        set.delete(emoji);
      } else {
        set.add(emoji);
      }
      const nextMap = { ...prev };
      if (set.size === 0) {
        delete nextMap[key];
      } else {
        nextMap[key] = Array.from(set);
      }
      return nextMap;
    });
    updateMessageReactionsLocally(chatId, { id: message.id, telegramMessageId: message.telegramMessageId }, next);

    try {
      if (isSwitching && currentMyReaction) {
        await chatService.removeReaction(chatId, messageId, currentMyReaction.emoji);
        await chatService.addReaction(chatId, messageId, emoji);
      } else if (removed) {
        await chatService.removeReaction(chatId, messageId, emoji);
      } else {
        await chatService.addReaction(chatId, messageId, emoji);
      }
    } catch (error) {
      const detail = extractBackendError(error);
      if (detail.includes('REACTION_INVALID')) {
        setBlockedReactions((prev) => {
          if (prev.includes(emoji)) return prev;
          const nextBlocked = [...prev, emoji];
          try {
            localStorage.setItem('blockedReactions', JSON.stringify(nextBlocked));
          } catch (storageError) {
            console.warn('Failed to persist blocked reactions', storageError);
          }
          return nextBlocked;
        });
      }
      console.error('Failed to update reaction', error);
      try {
        const refreshed = await chatService.getMessageReactions(chatId, messageId);
        syncMyReactionsForMessage(chatId, { id: message.id, telegramMessageId: message.telegramMessageId }, refreshed);
        updateMessageReactionsLocally(chatId, { id: message.id, telegramMessageId: message.telegramMessageId }, refreshed);
      } catch (refreshError) {
        console.error('Failed to refresh reactions after error', refreshError);
        updateMessageReactionsLocally(chatId, { id: message.id, telegramMessageId: message.telegramMessageId }, previous);
      }
    }
  }, [syncMyReactionsForMessage, updateMessageReactionsLocally]);

  const handleSendMessage = async (text: string, attachments?: File[], replyToMessageId?: number) => {
    if (!selectedChatId) return;

    // Create optimistic messages for attachments
    let optimisticMessages: Message[] = [];
    if (attachments && attachments.length > 0) {
      optimisticMessages = [{
        id: `temp-${Date.now()}`,
        text: text || '',
        sender: 'me',
        timestamp: new Date(),
        isRead: true,
        isSending: true,
        replyToMessageId,
        attachments: attachments.map((file) => ({
          type: file.type.startsWith('image/') ? 'image' : 'file',
          name: file.name,
          url: URL.createObjectURL(file)
        }))
      }];
    } else if (text) {
      // Optimistic message for text only
      optimisticMessages = [{
        id: `temp-${Date.now()}`,
        text: text,
        sender: 'me',
        timestamp: new Date(),
        isRead: true,
        isSending: true,
        replyToMessageId
      }];
    }

    if (optimisticMessages.length > 0) {
      // Immediately add optimistic messages to state
      setMessages(prev => ({
        ...prev,
        [selectedChatId]: [...(prev[selectedChatId] || []), ...optimisticMessages]
      }));
    }

    try {
      // Case 1: Multiple attachments -> sendMediaGroup
      if (attachments && attachments.length > 1) {
        await chatService.sendMediaGroup(
          selectedChatId,
          attachments,
          text, // Pass text as caption
          replyToMessageId
        );
      }
      // Case 2: Single attachment -> sendPhoto or sendDocument with caption
      else if (attachments && attachments.length === 1) {
        const file = attachments[0];
        if (file.type.startsWith('image/')) {
          await chatService.sendPhoto(selectedChatId, file, text, replyToMessageId);
        } else {
          await chatService.sendDocument(selectedChatId, file, text, replyToMessageId);
        }
      }
      // Case 3: No attachments, just text -> sendMessage
      else if (text) {
        await chatService.sendMessage(selectedChatId, text, replyToMessageId);
      }

      // Refresh messages - this will replace optimistic messages with real ones from server
      const fetchedMessages = await fetchMessages(selectedChatId);
      const latestMessage = fetchedMessages && fetchedMessages.length > 0
        ? fetchedMessages[fetchedMessages.length - 1]
        : null;

      if (latestMessage) {
        setChats(prevChats => {
          const updatedChats = prevChats.map(chat => (
            chat.id === selectedChatId
              ? { ...chat, lastMessage: latestMessage }
              : chat
          ));
          chatsCacheService.updateChat(selectedChatId, (chat) => {
            const updatedChat = updatedChats.find(c => c.id === selectedChatId);
            return updatedChat || chat;
          });
          return updatedChats;
        });
      } else {
        void refreshChatLastMessage(selectedChatId);
      }

    } catch (error) {
      console.error("Failed to send message", error);
      // Remove optimistic messages on failure
      setMessages(prev => ({
        ...prev,
        [selectedChatId]: (prev[selectedChatId] || []).filter(msg => !optimisticMessages.find(om => om.id === msg.id))
      }));
      const backendError = extractBackendError(error);
      const message = backendError
        ? `${backendError}\nОбратитесь к администратору, приложив скриншот с данной ошибкой`
        : "Не удалось отправить сообщение.\nОбратитесь к администратору, приложив скриншот с данной ошибкой";
      alert(message);
    }
  };

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    // Clear all authentication data
    localStorage.removeItem('token');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('username');
    localStorage.removeItem('password');
    localStorage.removeItem('rememberMe');
    localStorage.removeItem('chatDrafts');

    // Reset application state
    setIsAuthenticated(false);
    setChats([]);
    setSelectedChatId(null);
    setMessages({});
    setDraftsByChat({});

    // Clear chats cache
    chatsCacheService.clear();

    // Disconnect socket
    socketService.disconnect();
  };

  const handleDraftChange = React.useCallback((chatId: string, draft: { text: string; html: string } | null) => {
    setDraftsByChat(prev => {
      if (!draft || (!draft.text.trim() && !draft.html.trim())) {
        if (!prev[chatId]) return prev;
        const next = { ...prev };
        delete next[chatId];
        return next;
      }
      return { ...prev, [chatId]: draft };
    });
  }, []);

  const allowedReactions = React.useMemo(() => {
    if (blockedReactions.length === 0) return DEFAULT_ALLOWED_REACTIONS;
    return DEFAULT_ALLOWED_REACTIONS.filter((reaction) => !blockedReactions.includes(reaction));
  }, [blockedReactions]);

  return (
    <Router>
      <div className="App">
        <Routes>
          <Route path="/" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ChatPage
                chats={chats}
                selectedChatId={selectedChatId}
                setSelectedChatId={setSelectedChatId}
                messages={messages}
                draftsByChat={draftsByChat}
                onDraftChange={handleDraftChange}
                onSendMessage={handleSendMessage}
                onEditMessage={handleEditMessage}
                onDeleteMessage={handleDeleteMessage}
                onToggleReaction={handleToggleReaction}
                allowedReactions={allowedReactions}
                onUpdateUnreadCount={updateUnreadCount}
                onLoadOlderMessages={loadOlderMessages}
                onLogout={handleLogout}
                isChatsLoading={isChatsLoading}
                hasLoadedChats={hasLoadedChats}
              />
            </ProtectedRoute>
          } />
          <Route path="/chat/:chatId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ChatPage
                chats={chats}
                selectedChatId={selectedChatId}
                setSelectedChatId={setSelectedChatId}
                messages={messages}
                draftsByChat={draftsByChat}
                onDraftChange={handleDraftChange}
                onSendMessage={handleSendMessage}
                onEditMessage={handleEditMessage}
                onDeleteMessage={handleDeleteMessage}
                onToggleReaction={handleToggleReaction}
                allowedReactions={allowedReactions}
                onUpdateUnreadCount={updateUnreadCount}
                onLoadOlderMessages={loadOlderMessages}
                onLogout={handleLogout}
                isChatsLoading={isChatsLoading}
                hasLoadedChats={hasLoadedChats}
              />
            </ProtectedRoute>
          } />
          <Route path="/login" element={
            <LoginRoute isAuthenticated={isAuthenticated}>
              <Login onLogin={handleLogin} />
            </LoginRoute>
          } />
        </Routes>
      </div>
    </Router>
  );
};

export default App;
